import { z } from "zod";
import {
  CONTINENTS,
  DIFFICULTY_LEVELS,
  TRIP_TYPES,
  TRIP_STATUSES,
  TRIP_DATE_LIFECYCLES,
  ORDER_STATUSES,
  LEAD_STATUSES,
  USER_ROLES,
  COMMUNICATION_TYPES,
  COMMUNICATION_DIRECTIONS,
  ACTIVITY_TYPES,
  ORDER_ITEM_KINDS,
  BLOG_CATEGORIES,
  BLOG_STATUSES,
  TEAM_SECTION_TYPES,
  TRIP_STYLES,
  TRIP_KINDS,
  FULFILLMENT_MODES,
  DESTINATION_REGIONS,
  SUPPLIER_TYPES,
  SUPPLIER_PAYMENT_PROFILES,
  SERVICE_KINDS,
  COST_SCOPES,
  BOOKING_MODES,
  TRIP_COMPONENT_SEED_TARGETS,
  SERVICE_BOOKING_STATUSES,
  FLIGHT_ALLOTMENT_STATUSES,
  TASK_ENTITY_TYPES,
  TASK_KINDS,
  TASK_STATUSES,
  INVOICE_TYPES,
  PAYMENT_DIRECTIONS,
  PAYMENT_METHODS,
  SUPPLIER_INVOICE_STATUSES,
  DOCUMENT_KINDS,
  DOCUMENT_AUDIENCES,
  DOCUMENT_LANGS,
  INCIDENT_TYPES,
  ROOM_TYPES,
  INCIDENT_SEVERITIES,
  EXPENSE_CATEGORIES,
  COMPLAINT_TYPES,
  COMPLAINT_STATUSES,
  GUIDE_ASSIGNMENT_ROLES,
  GUIDE_ASSIGNMENT_STATUSES,
  GUIDE_AVAILABILITY_REASONS,
  CALC_CATEGORIES,
  CALC_SCOPES,
  CALC_MARGIN_MODES,
} from "../types";
import { normalizeMeals } from "../utils";

// ============================================================================
// COMMON SCHEMAS
// ============================================================================

/**
 * Safe integer coercion for form inputs.
 * Handles NaN (from empty number inputs with valueAsNumber: true), null, undefined,
 * and string numbers. Falls back to the provided default value.
 *
 * Behaves like z.number().int().default(defaultVal), but also handles NaN and null
 * which Zod's .default() does not — those come from JSON-serialized NaN (→ null)
 * when a user clears a numeric input with valueAsNumber: true.
 *
 * For .partial() update schemas: .optional() wraps this schema, so undefined
 * is caught by ZodOptional before the preprocessor runs — missing fields stay
 * undefined and are not updated.
 */
const safeInt = (defaultVal: number) =>
  z.preprocess((v) => {
    if (v === null || v === undefined || (typeof v === "number" && isNaN(v))) return defaultVal;
    const n = Number(v);
    return isNaN(n) ? defaultVal : n;
  }, z.number().int());

/**
 * Safe nullable integer for optional numeric fields (priceOriginal, readTime, etc.).
 * NaN/null/""/undefined → null (field is blank or not set).
 * Chain .pipe(z.number().min(X).nullable().optional()) to add constraints.
 */
const safeIntNullable = z.preprocess((v) => {
  if (v === null || v === undefined || v === "" || (typeof v === "number" && isNaN(v))) return null;
  const n = Number(v);
  return isNaN(n) ? null : n;
}, z.number().int().nullable().optional());

/**
 * Blank-tolerant value for optional string fields edited in admin forms.
 * DB rows load NULLs and react-hook-form holds "" for untouched inputs — both
 * must mean "not set", otherwise z.string().email().optional() etc. rejects the
 * whole save with errors the form may not even display (destination-teams bug).
 * Use as z.preprocess(blankToUndefined, z.string()...optional()).
 */
const blankToUndefined = (v: unknown) => (v === null || v === "" ? undefined : v);

export const paginationSchema = z.object({
  page: z.coerce.number().min(1).default(1),
  limit: z.coerce.number().min(1).max(100).default(20),
});

export const idParamSchema = z.object({
  id: z.string().min(1),
});

export const slugParamSchema = z.object({
  slug: z.string().min(1),
});

// ============================================================================
// DESTINATION SCHEMAS
// ============================================================================

export const highlightSchema = z.object({
  title: z.string(),
  description: z.string(),
  image: z.string().optional(),
  imageId: z.string().nullable().optional(),
});

export const regionSchema = z.object({
  name: z.string(),
  description: z.string(),
  image: z.string().optional(),
  imageId: z.string().nullable().optional(),
});

export const practicalInfoSchema = z.object({
  visa: z.string().optional(),
  currency: z.string().optional(),
  language: z.string().optional(),
  timezone: z.string().optional(),
  electricity: z.string().optional(),
  vaccination: z.string().optional(),
});

export const destinationCreateSchema = z.object({
  slug: z.string().min(1).max(100),
  name: z.string().min(1).max(200),
  nameSk: z.string().max(200).optional(),
  continent: z.enum(CONTINENTS),

  // Descriptions
  shortDescription: z.string().optional(),
  shortDescriptionSk: z.string().optional(),
  description: z.string().optional(),
  descriptionSk: z.string().optional(),

  // Travel info
  bestTimeToVisit: z.string().max(500).optional(),
  climateInfo: z.string().optional(),

  // Practical info
  practicalInfo: practicalInfoSchema.optional(),

  // Rich content
  highlights: z.array(highlightSchema).optional(),
  regions: z.array(regionSchema).optional(),
  cuisine: z.string().optional(),
  cuisineSk: z.string().optional(),
  activities: z.array(z.string()).optional(),
  whyWithUs: z
    .array(z.union([z.string(), z.object({ title: z.string(), description: z.string() })]))
    .optional(),

  // Rich media content
  socialReels: z
    .array(
      z.object({
        thumbnail: z.string().default(""),
        thumbnailId: z.string().nullable().optional(),
        title: z.string().default(""),
        href: z.string().optional(),
      }),
    )
    .optional(),

  testimonial: z
    .object({
      quote: z.string().optional(),
      author: z.string().optional(),
      authorImage: z.string().optional(),
      authorImageId: z.string().nullable().optional(),
      backgroundImage: z.string().optional(),
      backgroundImageId: z.string().nullable().optional(),
    })
    .optional(),

  videoPromo: z
    .object({
      title: z.string().optional(),
      description: z.string().optional(),
      thumbnail: z.string().optional(),
      thumbnailId: z.string().nullable().optional(),
      ctaText: z.string().optional(),
    })
    .optional(),

  weatherData: z
    .object({
      bestMonths: z.array(z.number().int().min(1).max(12)).default([]),
      goodMonths: z.array(z.number().int().min(1).max(12)).default([]),
      temperatures: z.record(
        z.object({
          air: z.number().optional(),
          water: z.number().optional(),
        }),
      ),
      description: z.string().optional(),
      // Per-month weather text keyed by month number ("1"–"12"), plain text.
      monthlyDescriptions: z.record(z.string()).optional(),
      // Short practical "local tip" shown under Climate, plain text.
      localTip: z.string().optional(),
    })
    .optional(),

  // FAQ (categorized)
  faqs: z
    .array(
      z.object({
        topic: z.string().default("General"),
        items: z.array(
          z.object({
            question: z.string(),
            answer: z.string(),
          }),
        ),
      }),
    )
    .optional(),

  // Media
  featuredImage: z.string().optional(),
  galleryImages: z.array(z.string()).optional(),
  heroVideoUrl: z.string().optional(),
  // New media system (FK ids) — additive
  featuredImageId: z.string().nullable().optional(),
  galleryImageIds: z.array(z.string()).optional(),
  additionalImageId: z.string().nullable().optional(),

  // Additional content
  additionalContent: z.string().optional(),
  additionalImage: z.string().optional(),

  // Relations
  parentId: z
    .string()
    .nullable()
    .optional()
    .transform((v) => (v === "" ? null : v)),
  guideId: z
    .string()
    .nullable()
    .optional()
    .transform((v) => (v === "" ? null : v)),
  garantId: z
    .string()
    .nullable()
    .optional()
    .transform((v) => (v === "" ? null : v)),

  // Default calculation pax (Block 5.1) — prefill for calculations.paxCount on this
  // destination's guided trips (chain: defaultMinPax ?? 10). Self-healing: blank/0/negative all
  // coerce to null (= "use the default 10") so a typed 0 can't 400 the whole destination save.
  defaultMinPax: z.preprocess((v) => {
    if (v === null || v === undefined || v === "" || (typeof v === "number" && isNaN(v)))
      return null;
    const n = Number(v);
    return !isNaN(n) && n >= 1 ? Math.trunc(n) : null;
  }, z.number().int().min(1).nullable().optional()),
  autoRouteLeads: z.boolean().optional(),

  // SEO
  metaTitle: z.string().max(60).optional(),
  metaDescription: z.string().max(160).optional(),

  // Region classification
  region: z.enum(DESTINATION_REGIONS).optional(),

  // Hero title (short decorative text)
  heroTitle: z.string().max(100).optional(),
  // Page heading (H1 below hero section)
  heading: z.string().max(500).optional(),

  // Related items (manually curated)
  relatedTrips: z.array(z.string()).optional(),
  relatedInspirations: z.array(z.string()).optional(),

  // GPS coordinates
  latitude: z.string().optional(),
  longitude: z.string().optional(),

  // Settings
  sortOrder: safeInt(0),
  isFeatured: z.boolean().default(false),
});

export const destinationUpdateSchema = destinationCreateSchema.partial();

// ============================================================================
// ACTIVITY SCHEMAS
// ============================================================================

export const activityCreateSchema = z.object({
  slug: z.string().min(1).max(100),
  title: z.string().min(1).max(300),
  shortDescription: z.string().max(500).optional(),
  description: z.string().optional(),
  featuredImage: z
    .string()
    .optional()
    .transform((v) => (v === "" ? undefined : v)),
  galleryImages: z.array(z.string()).optional(),
  featuredImageId: z.string().nullable().optional(),
  galleryImageIds: z.array(z.string()).optional(),
  testimonialAuthorImageId: z.string().nullable().optional(),
  duration: z.string().max(100).optional(),
  priceFrom: safeIntNullable.pipe(z.number().min(0).nullable().optional()),
  difficultyLevel: z.enum(DIFFICULTY_LEVELS).optional(),
  activityType: z.enum(ACTIVITY_TYPES).optional(),
  destinationId: z
    .string()
    .nullable()
    .optional()
    .transform((v) => (v === "" ? null : v)),
  location: z.string().max(200).optional(),
  included: z.array(z.string()).optional(),
  tags: z.array(z.string()).optional(),
  videos: z.array(z.object({ url: z.string(), title: z.string() })).optional(),
  tipsContent: z.string().optional(),
  tips: z
    .array(
      z.object({
        question: z.string().min(1),
        answer: z.string().min(1),
      }),
    )
    .optional(),
  testimonialQuote: z.string().optional(),
  testimonialAuthor: z.string().optional(),
  testimonialAuthorImage: z
    .string()
    .optional()
    .transform((v) => (v === "" ? undefined : v)),
  socialReels: z
    .array(
      z.object({
        thumbnail: z.string().default(""),
        thumbnailId: z.string().nullable().optional(),
        title: z.string().default(""),
        href: z.string().optional(),
      }),
    )
    .optional(),
  relatedItems: z.array(z.string()).optional(),
  heroTitle: z.string().max(100).optional(),
  heading: z.string().max(500).optional(),
  isFeatured: z.boolean().default(false),
  status: z.enum(TRIP_STATUSES).default("draft"),
  sortOrder: safeInt(0),
  metaTitle: z.string().max(60).optional(),
  metaDescription: z.string().max(160).optional(),
});

export const activityUpdateSchema = activityCreateSchema.partial();

export const activityFilterSchema = z.object({
  destination: z.string().optional(),
  activityType: z.enum(ACTIVITY_TYPES).optional(),
  search: z.string().optional(),
  ...paginationSchema.shape,
});

// ============================================================================
// BLOG POST SCHEMAS
// ============================================================================

export const blogPostCreateSchema = z.object({
  slug: z.string().min(1).max(200),
  title: z.string().min(1).max(300),
  excerpt: z.string().max(500).optional(),
  content: z.string().optional(),
  featuredImage: z
    .string()
    .optional()
    .transform((v) => (v === "" ? undefined : v)),
  galleryImages: z.array(z.string()).optional(),
  category: z.enum(BLOG_CATEGORIES),
  tags: z.array(z.string()).optional(),
  destinationId: z
    .string()
    .nullable()
    .optional()
    .transform((v) => (v === "" ? null : v)),
  relatedTripIds: z.array(z.string()).optional(),
  authorName: z.string().max(200).optional(),
  authorAvatar: z
    .string()
    .optional()
    .transform((v) => (v === "" ? undefined : v)),
  featuredImageId: z.string().nullable().optional(),
  galleryImageIds: z.array(z.string()).optional(),
  authorAvatarId: z.string().nullable().optional(),
  readTime: safeIntNullable.pipe(z.number().min(1).nullable().optional()),
  isFeatured: z.boolean().default(false),
  status: z.enum(BLOG_STATUSES).default("draft"),
  publishedAt: z.string().nullable().optional(),
  sortOrder: safeInt(0),
  metaTitle: z.string().max(60).optional(),
  metaDescription: z.string().max(160).optional(),
});

export const blogPostUpdateSchema = blogPostCreateSchema.partial();

export const blogPostFilterSchema = z.object({
  category: z.enum(BLOG_CATEGORIES).optional(),
  destination: z.string().optional(),
  search: z.string().optional(),
  featured: z.coerce.boolean().optional(),
  ...paginationSchema.shape,
});

// ============================================================================
// GUIDE SCHEMAS
// ============================================================================

export const guideCreateSchema = z.object({
  slug: z.string().min(1).max(100),
  name: z.string().min(1).max(200),
  photo: z.string().url().optional(),
  photoMediaId: z.string().nullable().optional(),
  bio: z.string().optional(),
  bioSk: z.string().optional(),
  livingIn: z.string().max(200).optional(),
  homeLocationId: z
    .string()
    .nullable()
    .optional()
    .transform((v) => (v === "" ? null : v)),
  homeDestinationId: z
    .string()
    .nullable()
    .optional()
    .transform((v) => (v === "" ? null : v)),
  yearsExperience: safeIntNullable.pipe(z.number().min(0).nullable().optional()),
  specializations: z.array(z.string()).optional(),
  youtubeChannelUrl: z.string().url().optional(),
  featuredVideos: z.array(z.string().url()).optional(),
  email: z.string().email().optional(),
  phone: z.string().max(30).optional(),
  whatsapp: z.string().max(30).optional(),
  isActive: z.boolean().default(true),
  sortOrder: safeInt(0),
});

export const guideUpdateSchema = guideCreateSchema.partial();

// ============================================================================
// TRIP SCHEMAS
// ============================================================================

// Meals can be either string[] (from editor) or {breakfast,lunch,dinner} object (legacy DB format)
const mealsSchema = z
  .union([z.array(z.string()), z.record(z.boolean().optional()).transform(normalizeMeals)])
  .optional();

export const itineraryDaySchema = z.object({
  day: z.number().int().min(1),
  title: z.string().min(1).max(200),
  description: z.string().optional(),
  image: z.string().optional(),
  imageId: z.string().nullable().optional(),
  accommodation: z.string().max(200).optional(),
  meals: mealsSchema,
  highlights: z.array(z.string()).optional(),
  guideTip: z.string().optional(),
});

/**
 * Validate trip kind specific required fields.
 * Tour requires durationDays >= 1; experience requires durationHours >= 1.
 * Used by tripCreateSchema (always) and tripUpdateSchema (only if tripKind in payload).
 */
const validateTripKindFields = (
  data: {
    tripKind?: (typeof TRIP_KINDS)[number];
    durationDays?: number | null;
    durationHours?: number | null;
  },
  ctx: z.RefinementCtx,
) => {
  if (data.tripKind === "experience") {
    if (!data.durationHours || data.durationHours < 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["durationHours"],
        message: "Required for experience",
      });
    }
  } else if (data.tripKind === "tour") {
    if (!data.durationDays || data.durationDays < 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["durationDays"],
        message: "Required for tour",
      });
    }
  }
};

const tripCreateBaseSchema = z.object({
  slug: z.string().min(1).max(100),
  title: z.string().min(1).max(300),
  titleSk: z.string().max(300).optional(),
  shortDescription: z.string().max(500).optional(),
  shortDescriptionSk: z.string().max(500).optional(),
  description: z.string().optional(),
  descriptionSk: z.string().optional(),
  priceFrom: safeInt(0).pipe(z.number().min(0)),
  priceOriginal: safeIntNullable.pipe(z.number().min(0).nullable().optional()),
  priceEur: safeIntNullable.pipe(z.number().min(0).nullable().optional()),
  durationDays: safeIntNullable.pipe(z.number().min(1).nullable().optional()),
  durationHours: safeIntNullable.pipe(z.number().min(1).max(24).nullable().optional()),
  difficultyLevel: z.enum(DIFFICULTY_LEVELS).optional(),
  groupSizeMin: safeInt(2).pipe(z.number().min(1)),
  groupSizeMax: safeInt(16).pipe(z.number().min(1)),
  tripType: z.enum(TRIP_TYPES),
  tripKind: z.enum(TRIP_KINDS).default("tour"),
  // Booking axis (Pre-launch Area 3) — independent of tripKind (content axis).
  fulfillmentMode: z.enum(FULFILLMENT_MODES).default("fixed_departures"),
  // ISO-2 countries the trip touches (Block 5.6 — country KB consumers). Multi-country
  // combos exist (VN+Laos); empty/absent reads as ["VN"] at the consumer.
  countryCodes: z
    .array(
      z
        .string()
        .length(2)
        .transform((s) => s.toUpperCase()),
    )
    .nullable()
    .optional(),
  availableWeekdays: z.array(z.number().int().min(0).max(6)).nullable().optional(),
  leadTimeHours: safeInt(24).pipe(z.number().min(0).max(720)),
  highlights: z
    .array(
      z.object({
        title: z.string().min(1),
        description: z.string().optional(),
        icon: z.string().optional(),
        image: z.string().optional(),
        imageId: z.string().nullable().optional(),
      }),
    )
    .optional(),
  schedule: z
    .array(
      z.object({
        time: z.string().min(1),
        title: z.string().min(1),
        description: z.string().optional(),
      }),
    )
    .optional(),
  guideId: z
    .string()
    .nullable()
    .optional()
    .transform((v) => (v === "" ? null : v)),
  destinationId: z
    .string()
    .nullable()
    .optional()
    .transform((v) => (v === "" ? null : v)),
  startLocationId: z
    .string()
    .nullable()
    .optional()
    .transform((v) => (v === "" ? null : v)),
  endLocationId: z
    .string()
    .nullable()
    .optional()
    .transform((v) => (v === "" ? null : v)),
  featuredImage: z
    .string()
    .optional()
    .transform((v) => (v === "" ? undefined : v)),
  galleryImages: z.array(z.string()).optional(),
  youtubeVideos: z.array(z.string()).optional(),
  guideVideoUrl: z
    .string()
    .optional()
    .transform((v) => (v === "" ? undefined : v)),
  featuredImageId: z.string().nullable().optional(),
  galleryImageIds: z.array(z.string()).optional(),
  mapImageId: z.string().nullable().optional(),
  itinerary: z.array(itineraryDaySchema).optional(),
  included: z.array(z.string()).optional(),
  excluded: z.array(z.string()).optional(),
  // Facultative on-site activities for the offer PDF (Block 5.6 Řez 2).
  optionalActivities: z
    .array(
      z.object({
        name: z.string().min(1).max(200),
        priceNote: z.string().max(200).optional(),
        description: z.string().max(500).optional(),
      }),
    )
    .optional(),
  note: z.string().optional(),
  guideRecommendation: z.string().optional(),
  // Default meeting point / sraz (Block 4.4) — tour standard inherited by departures + experiences.
  defaultMeetingPoint: z.string().max(200).nullable().optional(),
  defaultMeetingTime: z.string().max(40).nullable().optional(),
  faqs: z
    .array(
      z.object({
        topic: z.string().default("General"),
        items: z.array(
          z.object({
            question: z.string().min(1),
            answer: z.string().min(1),
          }),
        ),
      }),
    )
    .optional(),
  socialReels: z
    .array(
      z.object({
        thumbnail: z.string(),
        thumbnailId: z.string().nullable().optional(),
        title: z.string(),
        href: z.string().optional(),
      }),
    )
    .optional(),
  mapImage: z
    .string()
    .optional()
    .transform((v) => (v === "" ? undefined : v)),
  relatedItems: z.array(z.string()).optional(),
  heroTitle: z.string().max(100).optional(),
  heading: z.string().max(500).optional(),
  tripStyle: z.enum(TRIP_STYLES).optional(),
  isFeatured: z.boolean().default(false),
  isLastMinute: z.boolean().default(false),
  hasCzechGuide: z.boolean().default(true),
  allowsOnlineBooking: z.boolean().default(false),
  tags: z.array(z.string()).optional(),
  metaTitle: z.string().max(60).optional(),
  metaDescription: z.string().max(160).optional(),
  status: z.enum(TRIP_STATUSES).default("draft"),
});

export const tripCreateSchema = tripCreateBaseSchema.superRefine(validateTripKindFields);

export const tripUpdateSchema = tripCreateBaseSchema.partial().superRefine((data, ctx) => {
  // Apply tripKind validation only if tripKind is in the payload (otherwise partial update of unrelated fields)
  if (data.tripKind !== undefined) validateTripKindFields(data, ctx);
});

export const tripFilterSchema = z.object({
  destination: z.string().optional(),
  continent: z.enum(CONTINENTS).optional(),
  tripType: z.enum(TRIP_TYPES).optional(),
  difficultyLevel: z.enum(DIFFICULTY_LEVELS).optional(),
  priceMin: z.coerce.number().min(0).optional(),
  priceMax: z.coerce.number().min(0).optional(),
  durationMin: z.coerce.number().min(1).optional(),
  durationMax: z.coerce.number().min(1).optional(),
  hasCzechGuide: z.coerce.boolean().optional(),
  isFeatured: z.coerce.boolean().optional(),
  isLastMinute: z.coerce.boolean().optional(),
  tags: z.string().optional(), // comma-separated
  search: z.string().optional(),
  sortBy: z.enum(["price", "duration", "date", "popularity"]).default("popularity"),
  sortOrder: z.enum(["asc", "desc"]).default("asc"),
  ...paginationSchema.shape,
});

// ============================================================================
// TRIP DATE SCHEMAS
// ============================================================================

export const tripDateCreateSchema = z.object({
  tripId: z.string().min(1),
  startDate: z.coerce.date(),
  endDate: z.coerce.date(),
  price: z.number().int().min(0),
  priceEur: z.number().int().min(0).optional(),
  priceSingleRoom: z.number().int().min(0).optional(),
  capacity: z.number().int().min(1),
  bookedCount: z.number().int().min(0).default(0),
  guideId: z.string().optional(),
  lifecycle: z.enum(TRIP_DATE_LIFECYCLES).default("tentative"),
  notes: z.string().optional(),
  // Departure velín (Block 2 B2.3) — additive.
  waitlistCount: z.number().int().min(0).optional(),
  roomingNotes: z.string().nullable().optional(),
  sharedGuideId: z.string().nullable().optional(),
  // Season backbone (Block 4.1) — additive; preserved by syncTripDates on trip edit.
  code: z.string().max(40).nullable().optional(),
  leadUserId: z.string().nullable().optional(),
  minPax: safeIntNullable.pipe(z.number().min(0).nullable().optional()),
});

export const tripDateUpdateSchema = tripDateCreateSchema.partial();

// ============================================================================
// INQUIRY SCHEMA (Public form)
// ============================================================================

export const inquirySchema = z
  .object({
    tripId: z.string().min(1),
    tripDateId: z.string().optional(),
    // Empty string ("") is the form's default before a date is picked; coerce it to
    // undefined BEFORE the regex so an unselected date doesn't fail validation (it's
    // optional). Same empty-string idiom as orderItemSchema's id fields.
    requestedDate: z.preprocess(
      (v) => (v === "" ? undefined : v),
      z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, "Invalid date format")
        .optional(),
    ),
    firstName: z.string().min(1, "First name is required").max(100),
    lastName: z.string().min(1, "Last name is required").max(100),
    email: z.string().email("Invalid email address"),
    phone: z.string().min(5, "Phone number is required").max(20),
    adults: z.number().int().min(1).max(20).default(2),
    children: z.number().int().min(0).max(10).default(0),
    // Age of each child (Block 6.1 Řez 1a). Shown/required only when children >= 1; feeds
    // flight pricing (infant <2 / child <12). superRefine below requires one age per child.
    // `.max(10)` mirrors the `children` cap and bounds the array independently of the superRefine
    // branch (which is skipped when children=0) — a public endpoint must not accept an unbounded array.
    childAges: z.array(z.number().int().min(0).max(17)).max(10).optional(),
    notes: z.string().max(2000).optional(),
    utmSource: z.string().optional(),
    utmMedium: z.string().optional(),
    utmCampaign: z.string().optional(),
    clickId: z.string().max(512).optional(),
    clickSource: z.string().max(32).optional(),
    // Enriched qualification fields (Block 5.7 Řez 2 — Veru's wishlist). All optional;
    // empty string from an unselected <select> coerces to undefined.
    tripType: z.preprocess(
      (v) => (v === "" ? undefined : v),
      z.enum(["fit", "guided", "stay", "service"]).optional(),
    ),
    flightClass: z.preprocess(
      (v) => (v === "" ? undefined : v),
      z.enum(["economy", "premium", "business"]).optional(),
    ),
    budget: z.string().max(120).optional(),
    dateFlexibility: z.preprocess(
      (v) => (v === "" ? undefined : v),
      z.enum(["fixed", "plus_minus_week", "flexible"]).optional(),
    ),
    travelExperience: z.preprocess(
      (v) => (v === "" ? undefined : v),
      z.enum(["first_asia", "experienced", "expert"]).optional(),
    ),
  })
  .superRefine((data, ctx) => {
    // Require exactly one valid age per child when children are included (Block 6.1 Řez 1a).
    if ((data.children ?? 0) >= 1) {
      const ages = data.childAges ?? [];
      if (ages.length !== data.children) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Please enter the age of each child.",
          path: ["childAges"],
        });
      }
    }
  })
  // Transform to internal field names for API compatibility
  .transform((data) => ({
    ...data,
    adultsCount: data.adults,
    childrenCount: data.children,
  }));

// ============================================================================
// CUSTOMER SCHEMAS
// ============================================================================

export const customerCreateSchema = z.object({
  email: z.string().email(),
  phone: z.string().max(30).optional(),
  firstName: z.string().max(100).optional(),
  lastName: z.string().max(100).optional(),
  street: z.string().max(200).optional(),
  city: z.string().max(100).optional(),
  zip: z.string().max(20).optional(),
  country: z.string().length(2).default("CZ"),
  birthDate: z.coerce.date().optional(),
  passportNumber: z.string().max(50).optional(),
  passportExpiry: z.coerce.date().optional(),
  nationality: z.string().max(50).optional(),
  newsletterSubscribed: z.boolean().default(false),
  newsletterPreferences: z.array(z.string()).optional(),
  notes: z.string().optional(),
  source: z.string().max(50).optional(),
});

export const customerUpdateSchema = customerCreateSchema.partial();

// ============================================================================
// ORDER SCHEMAS
// ============================================================================

export const participantSchema = z.object({
  name: z.string().min(1).max(200),
  birthDate: z.string().optional(),
  passport: z.string().max(50).optional(),
  passportExpiry: z.string().optional(),
  nationality: z.string().max(50).optional(),
});

// A single order line item (snapshot — see schema/orderItems.ts).
export const orderItemSchema = z.object({
  id: z.string().optional(),
  kind: z.enum(ORDER_ITEM_KINDS).default("custom"),
  tripId: z
    .string()
    .nullable()
    .optional()
    .transform((v) => (v === "" ? null : v)),
  tripDateId: z
    .string()
    .nullable()
    .optional()
    .transform((v) => (v === "" ? null : v)),
  name: z.string().min(1).max(300),
  description: z.string().nullable().optional(),
  unitPrice: safeInt(0),
  quantity: safeInt(1).pipe(z.number().min(1)),
  // Currency removed — line items inherit the order's currency (krok 7 legacy drop).
  meta: z.record(z.unknown()).nullable().optional(),
  sortOrder: safeInt(0),
});

export const orderCreateSchema = z.object({
  customerId: z.string().min(1),
  // Optional: custom (catalog-less) orders have no trip; their price comes from items.
  tripId: z
    .string()
    .nullable()
    .optional()
    .transform((v) => (v === "" ? null : v)),
  tripDateId: z.string().optional(),
  // Lead this order was converted from (set by the conversion wizard).
  leadId: z
    .string()
    .nullable()
    .optional()
    .transform((v) => (v === "" ? null : v)),
  requestedDate: z.string().optional(),
  participants: z.array(participantSchema).min(1),
  adultsCount: z.number().int().min(1).default(2),
  childrenCount: z.number().int().min(0).default(0),
  // Derived from items by recomputeOrderTotal — optional on input (seed/legacy).
  totalPrice: z.number().int().min(0).optional(),
  // Currency is resolved server-side: explicit value > finance defaultCurrency > "CZK".
  // No default here so the backend can distinguish "not provided" from an explicit choice.
  currency: z.string().length(3).optional(),
  // Initial status chosen in the wizard — applied via the single-writer transition
  // (seat hold + timeline + workflows) instead of being written raw.
  status: z.enum(ORDER_STATUSES).optional(),
  // depositAmount is NOT accepted here — recomputeOrderTotal is the sole writer (derived from
  // booking settings); a wizard-sent value was silently dropped. depositDueDate is still honoured.
  depositDueDate: z.coerce.date().optional(),
  source: z.string().default("web_form"),
  customerNotes: z.string().optional(),
  internalNotes: z.string().optional(),
  // Line items — when present they define the price; otherwise a trip item is derived.
  items: z.array(orderItemSchema).optional(),
});

export type OrderItemInput = z.infer<typeof orderItemSchema>;

export const orderUpdateSchema = orderCreateSchema.partial();

export const orderStatusChangeSchema = z.object({
  status: z.enum(ORDER_STATUSES),
  note: z.string().optional(),
});

// ============================================================================
// LEAD SCHEMAS (inquiry funnel)
// ============================================================================

const nullableId = z
  .string()
  .nullable()
  .optional()
  .transform((v) => (v === "" ? null : v));

export const leadCreateSchema = z.object({
  status: z.enum(LEAD_STATUSES).default("received"),
  customerId: nullableId,
  contactName: z.string().max(200).optional(),
  contactEmail: z
    .string()
    .email()
    .optional()
    .or(z.literal("").transform(() => undefined)),
  contactPhone: z.string().max(30).optional(),
  tripId: nullableId,
  destinationId: nullableId,
  requestedDate: z.string().optional(),
  paxAdults: safeInt(2).pipe(z.number().min(1)),
  paxChildren: safeInt(0).pipe(z.number().min(0)),
  estimatedValue: safeIntNullable.pipe(z.number().min(0).nullable().optional()),
  currency: z.string().length(3).default("CZK"),
  assignedTo: nullableId,
  source: z.string().max(50).default("web_form"),
  utmSource: z.string().optional(),
  utmMedium: z.string().optional(),
  utmCampaign: z.string().optional(),
  firstTouchSource: z.string().optional(),
  firstTouchCampaign: z.string().optional(),
  lastTouchSource: z.string().optional(),
  lastTouchCampaign: z.string().optional(),
  clickId: z.string().optional(),
  // Click-ID provider (gclid/fbclid/…) — the `leads.clickSource` column and createOrder's
  // attribution inheritance both expect it; without it the admin create/update path dropped it.
  clickSource: z.string().max(32).optional(),
  nextActionAt: z.string().nullable().optional(),
  leadScore: safeInt(0),
  lostReason: z.string().optional(),
  convertedOrderId: nullableId,
  customerNotes: z.string().optional(),
  internalNotes: z.string().optional(),
});

export const leadUpdateSchema = leadCreateSchema.partial();

export type LeadCreateInput = z.infer<typeof leadCreateSchema>;

// ============================================================================
// SUPPLIER SCHEMAS (Block 2 — operations)
// ============================================================================

export const supplierCreateSchema = z.object({
  name: z.string().min(1).max(200),
  type: z.enum(SUPPLIER_TYPES).default("other"),
  contactName: z.string().max(200).nullable().optional(),
  contactEmail: z
    .string()
    .email()
    .nullable()
    .optional()
    .or(z.literal("").transform(() => null)),
  contactPhone: z.string().max(30).nullable().optional(),
  currency: z.string().length(3).default("CZK"),
  paymentProfile: z.enum(SUPPLIER_PAYMENT_PROFILES).default("invoice"),
  bookingMode: z.enum(BOOKING_MODES).default("confirm_required"),
  paymentTerms: safeIntNullable.pipe(z.number().min(0).max(365).nullable().optional()),
  commissionPct: safeIntNullable.pipe(z.number().min(0).max(100).nullable().optional()),
  destinationId: nullableId,
  notes: z.string().nullable().optional(),
  isActive: z.boolean().default(true),
  // Per-supplier email-template override (Block 4.12 Řez 2) — a templates.slug, or null/"" for the
  // default kind-aware booking-request builder.
  emailTemplateSlug: z
    .string()
    .max(120)
    .nullable()
    .optional()
    .or(z.literal("").transform(() => null)),
});

export const supplierUpdateSchema = supplierCreateSchema.partial();

export type SupplierCreateInput = z.infer<typeof supplierCreateSchema>;

// One room-type price variant of a catalog component (Block 6.1 Řez 2b). `id` is present for
// existing rows (id-diff sync on save), absent for newly-added ones. `cost` is the PER-ROOM buy
// cost (whole room); `currency` null → inherit the component's currency.
export const supplierComponentRoomTypeSchema = z.object({
  id: z.string().optional(),
  roomType: z.enum(ROOM_TYPES),
  cost: safeInt(0).pipe(z.number().min(0)),
  currency: z.string().length(3).nullable().optional(),
  sortOrder: safeInt(0),
});
export type SupplierComponentRoomTypeInput = z.infer<typeof supplierComponentRoomTypeSchema>;

export const supplierComponentCreateSchema = z.object({
  supplierId: nullableId,
  destinationId: nullableId,
  name: z.string().min(1).max(200),
  kind: z.enum(SERVICE_KINDS).default("other"),
  sellPrice: safeInt(0).pipe(z.number().min(0)),
  cost: safeInt(0).pipe(z.number().min(0)),
  currency: z.string().length(3).default("CZK"),
  costScope: z.enum(COST_SCOPES).default("per_pax"),
  bookingMode: z.enum(BOOKING_MODES).default("confirm_required"),
  notes: z.string().nullable().optional(),
  isActive: z.boolean().default(true),
  // Room-type variants (Řez 2b) — a child collection synced by the route's create/update hooks,
  // NOT a column on the component row. Optional (no .default) so a .partial() update that omits it
  // leaves the existing variants untouched; an explicit [] clears them.
  roomTypes: z.array(supplierComponentRoomTypeSchema).optional(),
});

export const supplierComponentUpdateSchema = supplierComponentCreateSchema.partial();

export type SupplierComponentCreateInput = z.infer<typeof supplierComponentCreateSchema>;

// ============================================================================
// TRIP COMPONENT SCHEMA (Block 2 — trip "kusovník" / bill-of-materials template)
// ============================================================================

// One template line on a trip's kusovník. The trip editor sends an array of these; the
// API replaces the trip's template (syncTripComponents) and auto-seeds bookings from it.
export const tripComponentSchema = z.object({
  id: z.string().optional(),
  componentId: z.string().min(1),
  quantity: safeInt(1).pipe(z.number().min(1)),
  seedTarget: z.enum(TRIP_COMPONENT_SEED_TARGETS).default("order"),
  sortOrder: safeInt(0),
  notes: z.string().max(500).nullable().optional(),
});

export const tripComponentsReplaceSchema = z.object({
  components: z.array(tripComponentSchema),
});

export type TripComponentInput = z.infer<typeof tripComponentSchema>;

// ============================================================================
// CALCULATION SCHEMAS (Block 5.1 — pricing: costs → markup → sell price)
// ============================================================================

/** Like safeInt but allows fractions — calc item quantity is real (0.5 guide days). */
const safeNumber = (defaultVal: number) =>
  z.preprocess((v) => {
    if (v === null || v === undefined || (typeof v === "number" && isNaN(v))) return defaultVal;
    const n = Number(v);
    return isNaN(n) ? defaultVal : n;
  }, z.number());

// One cost line. cost is WHOLE units of `currency` (formatMoney convention).
export const calculationItemSchema = z.object({
  id: z.string().optional(),
  name: z.string().min(1).max(300),
  category: z.enum(CALC_CATEGORIES),
  componentId: nullableId,
  cost: safeInt(0).pipe(z.number().min(0)),
  currency: z.string().length(3),
  scope: z.enum(CALC_SCOPES),
  quantity: safeNumber(1).pipe(z.number().gt(0)),
  occupancy: safeInt(2).pipe(z.number().min(1)),
  dayIndex: safeIntNullable.pipe(z.number().min(1).nullable().optional()),
  notes: z.string().max(500).nullable().optional(),
  sortOrder: safeInt(0),
});

export type CalculationItemInput = z.infer<typeof calculationItemSchema>;

const sellLineSchema = z.object({
  label: z.string().min(1).max(120),
  itemIds: z.array(z.string()),
  includeMargin: z.boolean().default(true),
});

const paymentSplitRowSchema = z.object({
  kind: z.enum(["deposit", "insurance", "balance"]),
  amountType: z.enum(["fixed", "pct"]),
  value: z.number().min(0),
  dueRule: z.string().max(200).optional(),
});

const calculationBaseSchema = z.object({
  title: z.string().min(1).max(200).default("Calculation"),
  // paxCount optional on create — the API prefills via destinations.defaultMinPax ?? 10
  // (guided) or the order's pax (FIT). Never from trips.groupSizeMin (marketing default 2).
  paxCount: safeIntNullable.pipe(z.number().min(1).nullable().optional()),
  // No default — the create route labels by anchor (trip → "markup", order → "commission").
  marginMode: z.enum(CALC_MARGIN_MODES).optional(),
  // Stored as a fraction (0.309 = 30.9 %).
  marginPct: safeNumber(0.3).pipe(z.number().min(0).max(5)),
  currency: z.string().length(3).optional(),
  // money.ts format: "1 unit of X = N CZK". Optional on create — snapshotted from finance settings.
  fxRates: z.record(z.number().gt(0)).optional(),
  intlFlightPerPax: safeInt(0).pipe(z.number().min(0)),
  roundingStep: safeInt(10).pipe(z.number().min(1)),
  singleSupplementOverride: safeIntNullable.pipe(z.number().min(0).nullable().optional()),
  sensitivityPax: z.array(z.number().int().min(1)).max(12).nullable().optional(),
  sellLines: z.array(sellLineSchema).max(20).nullable().optional(),
  paymentSplit: z.array(paymentSplitRowSchema).max(10).nullable().optional(),
  notes: z.string().max(2000).nullable().optional(),
});

export const calculationCreateSchema = calculationBaseSchema
  .extend({
    tripId: nullableId,
    orderId: nullableId,
  })
  .refine((v) => Boolean(v.tripId) !== Boolean(v.orderId), {
    message: "Provide exactly one of tripId or orderId",
  });

// Header + the full items array in one save (array-diff server-side, syncTripComponents
// pattern). Anchors are immutable after create; status moves via the activate route.
export const calculationUpdateSchema = calculationBaseSchema.partial().extend({
  items: z.array(calculationItemSchema).max(200).optional(),
});

export type CalculationCreateInput = z.infer<typeof calculationCreateSchema>;
export type CalculationUpdateInput = z.infer<typeof calculationUpdateSchema>;

// Stateless preview payload for the live editor — same shapes, nothing persisted.
// insuranceDays/insuranceProduct let the live editor quote insurance for unsaved state.
export const calculationPreviewSchema = calculationBaseSchema.partial().extend({
  paxCount: safeInt(10).pipe(z.number().min(1)),
  currency: z.string().length(3),
  fxRates: z.record(z.number().gt(0)),
  items: z.array(calculationItemSchema).max(200).default([]),
  insuranceDays: safeIntNullable.pipe(z.number().min(1).nullable().optional()),
  insuranceProduct: z.string().max(40).nullable().optional(),
});

// Versioned insurance price table (settings key `insurance.config`). Editing appends a
// new version (validFrom) — versions are never rewritten (insurer rate-change audit).
const insuranceRowSchema = z.object({
  maxTripPricePerPax: z.number().int().min(1),
  maxDays: z.number().int().min(1),
  perPax: z.number().int().min(0),
});

const insuranceVersionSchema = z.object({
  // Zero-padded ISO date so lexical order == chronological order — activeVersion() filters and
  // sorts versions by string compare, which is only correct for fixed-width YYYY-MM-DD.
  validFrom: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "validFrom must be a zero-padded ISO date (YYYY-MM-DD)"),
  rows: z.array(insuranceRowSchema).min(1).max(50),
});

const insuranceProductSchema = z.object({
  code: z.string().min(1).max(40),
  label: z.string().min(1).max(200),
  versions: z.array(insuranceVersionSchema).min(1).max(100),
});

export const insuranceConfigSchema = z.object({
  products: z.array(insuranceProductSchema).max(50),
});

export type InsuranceConfig = z.infer<typeof insuranceConfigSchema>;
export type InsuranceProduct = z.infer<typeof insuranceProductSchema>;

// ============================================================================
// PASSPORT SCAN / PNR SCHEMAS (Block 5.2)
// ============================================================================

// Confirm writes the scan's extracted fields into the participant; `fields` lets the
// operator correct individual values in the same gesture (still their explicit action —
// nothing is ever written blind).
export const passportScanConfirmSchema = z.object({
  participantId: z.string().min(1),
  fields: z
    .object({
      firstName: z.string().min(1).max(100),
      lastName: z.string().min(1).max(100),
      passportNumber: z.string().min(1).max(20),
      nationality: z.string().max(3),
      birthDate: z.string().min(8).max(10),
      expiryDate: z.string().min(8).max(10),
    })
    .partial()
    .optional(),
});

export const pnrParseRequestSchema = z.object({
  text: z.string().min(1).max(20000),
});

export const passportMarkCheckedSchema = z.object({
  checked: z.boolean(),
});

export type PassportScanConfirmInput = z.infer<typeof passportScanConfirmSchema>;

// ============================================================================
// ORDER PARTICIPANT SCHEMA (Block 2 — per-pax table, rozhodnutí B)
// ============================================================================

// Rich per-pax shape (the admin pax editor / customer portal send this). Distinct from
// the legacy `participantSchema` (single `name`) which the web inquiry path still uses.
export const orderParticipantSchema = z.object({
  id: z.string().optional(),
  customerId: nullableId,
  firstName: z.string().max(100).nullable().optional(),
  lastName: z.string().max(100).nullable().optional(),
  birthDate: z.string().nullable().optional(),
  nationality: z.string().max(50).nullable().optional(),
  // Entry-portal demographics (B5.4) — Cambodia e-Arrival requires both; "" → null.
  gender: z
    .preprocess((v) => (v === "" ? null : v), z.enum(["male", "female", "other"]).nullable())
    .optional(),
  occupation: z.string().max(120).nullable().optional(),
  passportNumber: z.string().max(50).nullable().optional(),
  passportExpiry: z.string().nullable().optional(),
  dietary: z.string().max(300).nullable().optional(),
  isLeadPax: z.boolean().optional(),
  sortOrder: safeInt(0),
});

export const orderParticipantsReplaceSchema = z.object({
  participants: z.array(orderParticipantSchema),
});

export type OrderParticipantInput = z.infer<typeof orderParticipantSchema>;

// Inline room-type edit from the departure manifest/grid (Block 4.3). Ops-managed field written
// DIRECTLY to the participant row (preserved by syncParticipants), not via the order-JSON editor —
// so it deliberately lives OUTSIDE orderParticipantSchema.
export const participantRoomPatchSchema = z.object({
  roomType: z.enum(ROOM_TYPES).nullable(),
});
export type ParticipantRoomPatchInput = z.infer<typeof participantRoomPatchSchema>;

// ============================================================================
// SERVICE BOOKING SCHEMAS (Block 2 — universal supplier services)
// ============================================================================

// `snapshotName`/`cost` are optional on input — the API snapshots them from the
// chosen catalog component (componentId) when absent (beforeCreate hook).
export const serviceBookingCreateSchema = z.object({
  orderId: nullableId,
  tripDateId: nullableId,
  supplierId: nullableId,
  componentId: nullableId,
  kind: z.enum(SERVICE_KINDS).default("other"),
  status: z.enum(SERVICE_BOOKING_STATUSES).default("draft"),
  snapshotName: z.string().max(200).nullable().optional(),
  cost: safeIntNullable.pipe(z.number().min(0).nullable().optional()),
  // currency + costScope are SNAPSHOT fields — left optional (no Zod default) so an omitted value
  // resolves from the component (or supplier) in the create hook. A Zod `.default()` here would fill
  // the field BEFORE the hook runs, defeating the `?? comp.x` snapshot and silently forcing CZK /
  // per_pax onto every booking regardless of the catalogue. DB column defaults remain the final fallback.
  currency: z.string().length(3).optional(),
  costScope: z.enum(COST_SCOPES).optional(),
  // Chosen room-type variant (Block 6.1 Řez 2b) — when set with a componentId, the create hook
  // snapshots the matching room-type row's per-room cost into `cost`. null = no room-type choice.
  roomType: z.enum(ROOM_TYPES).nullable().optional(),
  // Snapshot-resolved by the create hook (component → supplier → confirm_required) when omitted.
  bookingMode: z.enum(BOOKING_MODES).optional(),
  actualCost: safeIntNullable.pipe(z.number().min(0).nullable().optional()),
  supplierDepositAmount: safeIntNullable.pipe(z.number().min(0).nullable().optional()),
  supplierBalanceDue: safeIntNullable.pipe(z.number().min(0).nullable().optional()),
  paxCount: safeInt(1).pipe(z.number().min(0)),
  confirmDeadline: z.string().nullable().optional(),
  paxCountDeadline: z.string().nullable().optional(),
  paymentDeadline: z.string().nullable().optional(),
  assignedTo: nullableId,
  // Block 4.10 — drawdown link: a flight booking can draw its seats from a group allotment.
  allotmentId: nullableId,
  details: z.record(z.unknown()).nullable().optional(),
  notes: z.string().nullable().optional(),
  // Customization marker (Block 4.5) — deviation from standard, shown to the guide. null = standard.
  customizationNote: z.string().max(1000).nullable().optional(),
});

export const serviceBookingUpdateSchema = serviceBookingCreateSchema.partial();

export type ServiceBookingCreateInput = z.infer<typeof serviceBookingCreateSchema>;

// Flight allotment (Block 4.10 / Téma 6) — a group-seat block on one departure.
export const flightAllotmentCreateSchema = z.object({
  airline: z.string().min(1).max(120),
  label: z.string().min(1).max(200),
  pnr: z.string().max(60).nullable().optional(),
  tripDateId: nullableId,
  seatsTotal: safeInt(0).pipe(z.number().min(0)),
  pricePerSeat: safeIntNullable.pipe(z.number().min(0).nullable().optional()),
  currency: z.string().length(3).default("USD"),
  depositAmount: safeIntNullable.pipe(z.number().min(0).nullable().optional()),
  depositDeadline: z.string().nullable().optional(),
  focReleaseDeadline: z.string().nullable().optional(),
  ticketingDeadline: z.string().nullable().optional(),
  status: z.enum(FLIGHT_ALLOTMENT_STATUSES).default("active"),
  assignedTo: nullableId,
  notes: z.string().nullable().optional(),
});

export const flightAllotmentUpdateSchema = flightAllotmentCreateSchema.partial();

export type FlightAllotmentCreateInput = z.infer<typeof flightAllotmentCreateSchema>;

// Monitored flight (Block 6.1 Řez 2a) — a flight watched for live status on a departure. Only
// the user-settable fields; the status snapshot (status/times/delay) is filled by the poll.
export const monitoredFlightCreateSchema = z.object({
  tripDateId: nullableId,
  allotmentId: nullableId,
  direction: z.enum(["outbound", "return", "other"]).default("outbound"),
  flightNumber: z
    .string()
    .min(2)
    .max(10)
    .transform((s) => s.toUpperCase().replace(/\s+/g, ""))
    // Constrain to a valid IATA-style designator (alphanumeric only). Runs on the normalised value
    // so it rejects "." / "/" and any other char — closes the in-host path-traversal of the
    // AeroDataBox URL ("../<date>") and stops bad data triggering useless paid API calls.
    .refine((s) => /^[A-Z0-9]{2,10}$/.test(s), "Flight number must be alphanumeric"),
  flightDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Date must be YYYY-MM-DD"),
});

export const monitoredFlightUpdateSchema = monitoredFlightCreateSchema.partial();

export type MonitoredFlightCreateInput = z.infer<typeof monitoredFlightCreateSchema>;

// ============================================================================
// TASK SCHEMAS (Block 2 — polymorphic worklist)
// ============================================================================

// Manual task creation from the admin (the engine inserts auto/deadline tasks directly,
// bypassing this schema). `dedupeKey`/`source` are engine-owned and not accepted here.
export const taskCreateSchema = z.object({
  entityType: z.enum(TASK_ENTITY_TYPES),
  entityId: z.string().min(1),
  kind: z.enum(TASK_KINDS).default("manual"),
  title: z.string().min(1).max(300),
  description: z.string().max(2000).nullable().optional(),
  dueDate: z.string().nullable().optional(),
  status: z.enum(TASK_STATUSES).default("open"),
  assignedTo: nullableId,
});

export const taskUpdateSchema = taskCreateSchema.partial();

// Status transition for a task (complete / snooze / reopen / cancel). `snoozedUntil` is
// only meaningful when status is "snoozed".
export const taskStatusSchema = z.object({
  status: z.enum(TASK_STATUSES),
  snoozedUntil: z.string().nullable().optional(),
});

export type TaskCreateInput = z.infer<typeof taskCreateSchema>;

// Back-scheduling rule (vision §5.6 "nastavená jednou") — kept in `ops` settings.
export const deadlineRuleSchema = z.object({
  key: z.string().min(1).max(50),
  title: z.string().min(1).max(200),
  daysBeforeDeparture: z.number().int().min(0).max(365),
});

export const opsSettingsSchema = z.object({
  deadlineRules: z.array(deadlineRuleSchema).optional(),
  deadlineScanWindowDays: z.number().int().min(1).max(120).optional(),
  passportExpiryWarnDays: z.number().int().min(0).max(1000).optional(),
});

export type OpsSettingsInput = z.infer<typeof opsSettingsSchema>;

// ============================================================================
// FINANCE SCHEMAS (Block 2 B2.5 — invoices, payments, supplier invoices)
// ============================================================================

// Issue an invoice from an order. Items are snapshotted server-side from `order_items`
// unless explicitly overridden (e.g. a deposit proforma at a custom amount).
export const invoiceIssueSchema = z.object({
  orderId: z.string().min(1),
  type: z.enum(INVOICE_TYPES).default("proforma"),
  dueDate: z.string().nullable().optional(),
  // Optional amount override for proforma deposits (minor units). When set, a single
  // line item for this amount is issued instead of snapshotting all order items.
  amountOverride: safeIntNullable.pipe(z.number().min(0).nullable().optional()),
  note: z.string().max(500).nullable().optional(),
});

// Record a payment on the unified ledger (lib/paymentsLedger.recordPayment).
export const paymentCreateSchema = z.object({
  direction: z.enum(PAYMENT_DIRECTIONS).default("in"),
  orderId: nullableId,
  invoiceId: nullableId,
  supplierInvoiceId: nullableId,
  amount: safeInt(0).pipe(z.number().min(1)),
  currency: z.string().length(3).default("CZK"),
  method: z.enum(PAYMENT_METHODS).default("bank"),
  paidAt: z.string().nullable().optional(),
  note: z.string().max(500).nullable().optional(),
});

export const supplierInvoiceCreateSchema = z.object({
  supplierId: nullableId,
  serviceBookingId: nullableId,
  number: z.string().max(100).nullable().optional(),
  amount: safeInt(0).pipe(z.number().min(0)),
  currency: z.string().length(3).default("CZK"),
  dueDate: z.string().nullable().optional(),
  status: z.enum(SUPPLIER_INVOICE_STATUSES).default("received"),
  note: z.string().max(500).nullable().optional(),
});

export const supplierInvoiceUpdateSchema = supplierInvoiceCreateSchema.partial();

export type InvoiceIssueInput = z.infer<typeof invoiceIssueSchema>;
export type PaymentCreateInput = z.infer<typeof paymentCreateSchema>;
export type SupplierInvoiceCreateInput = z.infer<typeof supplierInvoiceCreateSchema>;

// ============================================================================
// DOCUMENT SCHEMAS (Block 2 B2.4 — generated PDFs + e-signature)
// ============================================================================

// Generate a document from an order (lib/documentGen.generateDocument). The PDF is
// rendered server-side from the matching `templates` slug (or a built-in default); the
// signer defaults to the order's customer when `requiresSignature` is set.
export const documentGenerateSchema = z.object({
  orderId: z.string().min(1),
  kind: z.enum(DOCUMENT_KINDS).default("contract"),
  audience: z.enum(DOCUMENT_AUDIENCES).default("client"),
  lang: z.enum(DOCUMENT_LANGS).default("en"),
  templateId: nullableId,
  requiresSignature: z.boolean().default(false),
  signerName: z.string().max(200).nullable().optional(),
  signerEmail: z.string().email().max(200).nullable().optional(),
});

// Send an existing document for signature: ensures a signature row + emails the sign link.
export const documentSendSchema = z.object({
  signerName: z.string().max(200).nullable().optional(),
  signerEmail: z.string().email().max(200).nullable().optional(),
});

// Public signing submission (routes/public/sign.ts). The signer types their full name as
// the electronic signature (eIDAS SES); IP/userAgent are captured server-side.
export const signSubmitSchema = z.object({
  signerName: z.string().min(2).max(200),
  consent: z.literal(true),
});

export type DocumentGenerateInput = z.infer<typeof documentGenerateSchema>;
export type DocumentSendInput = z.infer<typeof documentSendSchema>;
export type SignSubmitInput = z.infer<typeof signSubmitSchema>;

// ============================================================================
// DEPARTURE VELÍN SCHEMAS (Block 2 B2.3 — group-departure control room)
// ============================================================================

// Ops-editable fields on a departure (the tripDate promoted to a velín). Guide assignment is
// NOT here (Block 4.2, R-A) — guideId/sharedGuideId are a sync-only mirror of confirmed
// guide_assignments, written solely by syncDepartureGuides. Set the velín guide via the
// schedule route (PUT /api/admin/schedule/departures/:id/guide), never this PATCH.
export const departureUpdateSchema = z.object({
  capacity: safeIntNullable.pipe(z.number().min(0).nullable().optional()),
  lifecycle: z.enum(TRIP_DATE_LIFECYCLES).optional(),
  waitlistCount: safeIntNullable.pipe(z.number().min(0).nullable().optional()),
  roomingNotes: z.string().max(5000).nullable().optional(),
  notes: z.string().max(5000).nullable().optional(),
  // Season backbone (Block 4.1) — code (unique, nullable), garant, go/no-go threshold.
  code: z.string().max(40).nullable().optional(),
  leadUserId: nullableId,
  minPax: safeIntNullable.pipe(z.number().min(0).nullable().optional()),
  // Personal guide message shown on the customer portal (Block 4.3 / 4.2 dotažení).
  guideMessage: z.string().max(2000).nullable().optional(),
  // Structured meeting point / sraz (Block 4.4) — point/time override the trip default; note is departure-only.
  meetingPoint: z.string().max(200).nullable().optional(),
  meetingTime: z.string().max(40).nullable().optional(),
  meetingNote: z.string().max(1000).nullable().optional(),
  // Pre-departure info meeting (Block 5.6 Řez 4) — when/link/done; meeting itself stays on Meet.
  infoMeetAt: z.string().max(40).nullable().optional(),
  infoMeetLink: z.string().max(500).nullable().optional(),
  infoMeetDoneAt: z.string().max(40).nullable().optional(),
});

// Bulk message to every customer on a departure (sent via Gmail).
export const departureBroadcastSchema = z.object({
  subject: z.string().min(1).max(300),
  body: z.string().min(1).max(10000),
});

export type DepartureUpdateInput = z.infer<typeof departureUpdateSchema>;
export type DepartureBroadcastInput = z.infer<typeof departureBroadcastSchema>;

// ============================================================================
// GUIDE SCHEDULE SCHEMAS (Vlna 2 / Block 4.2 — rozvrh průvodců)
// ============================================================================

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Invalid date format (YYYY-MM-DD)");

// Locations — lightweight city/hub primitive (R2).
export const locationCreateSchema = z.object({
  name: z.string().min(1).max(120),
  region: z.string().max(40).nullable().optional(),
  lat: z.number().min(-90).max(90).nullable().optional(),
  lng: z.number().min(-180).max(180).nullable().optional(),
  sortOrder: safeInt(0),
});
export const locationUpdateSchema = locationCreateSchema.partial();

// One directed gap override (from → to). Symmetry is the caller's job (seed both directions).
export const locationGapUpsertSchema = z.object({
  fromLocationId: z.string().min(1),
  toLocationId: z.string().min(1),
  minGapDays: safeInt(1).pipe(z.number().min(0).max(60)),
});

// Guide assignment — who guides what (R1). Dates/locations derive from the tripDate/trip.
export const guideAssignmentCreateSchema = z.object({
  guideId: z.string().min(1),
  tripDateId: z.string().min(1),
  role: z.enum(GUIDE_ASSIGNMENT_ROLES).default("lead"),
  status: z.enum(GUIDE_ASSIGNMENT_STATUSES).default("tentative"),
  notes: z.string().max(2000).nullable().optional(),
});
// guideId/tripDateId may change (swap/reassign); identity is the row id, not (guide, date).
export const guideAssignmentUpdateSchema = guideAssignmentCreateSchema.partial();

// Swap two assignments' guides in one step (timeline "prohodit").
export const guideAssignmentSwapSchema = z.object({
  assignmentAId: z.string().min(1),
  assignmentBId: z.string().min(1),
});

// Velín quick-confirm (R-A): set the confirmed lead/co guide on a departure. guideId null =
// remove the confirmed assignment of that role. Encapsulates upsert + single-confirmed enforce.
export const departureGuideSetSchema = z.object({
  role: z.enum(["lead", "co"]),
  guideId: z
    .string()
    .nullable()
    .transform((v) => (v === "" ? null : v)),
});

// Guide unavailability window (vacation/sick/blocked) — engine treats as a hard block.
export const guideAvailabilityCreateSchema = z
  .object({
    guideId: z.string().min(1),
    fromDate: isoDate,
    toDate: isoDate,
    reason: z.enum(GUIDE_AVAILABILITY_REASONS).default("vacation"),
    notes: z.string().max(2000).nullable().optional(),
  })
  .refine((d) => d.toDate >= d.fromDate, {
    message: "toDate must be on or after fromDate",
    path: ["toDate"],
  });
export const guideAvailabilityUpdateSchema = z.object({
  guideId: z.string().min(1).optional(),
  fromDate: isoDate.optional(),
  toDate: isoDate.optional(),
  reason: z.enum(GUIDE_AVAILABILITY_REASONS).optional(),
  notes: z.string().max(2000).nullable().optional(),
});

export type LocationCreateInput = z.infer<typeof locationCreateSchema>;
export type LocationUpdateInput = z.infer<typeof locationUpdateSchema>;
export type LocationGapUpsertInput = z.infer<typeof locationGapUpsertSchema>;
export type GuideAssignmentCreateInput = z.infer<typeof guideAssignmentCreateSchema>;
export type GuideAssignmentUpdateInput = z.infer<typeof guideAssignmentUpdateSchema>;
export type GuideAssignmentSwapInput = z.infer<typeof guideAssignmentSwapSchema>;
export type GuideAvailabilityCreateInput = z.infer<typeof guideAvailabilityCreateSchema>;
export type GuideAvailabilityUpdateInput = z.infer<typeof guideAvailabilityUpdateSchema>;

// ============================================================================
// PORTAL SCHEMAS (Block 2 B2.6 — customer web portal + offer, token-gated)
// ============================================================================

// A traveller filled in by the CUSTOMER themselves via the portal (rich per-pax, the
// data subject seeing their own data — no masking). Maps to order_participants.
export const portalParticipantSchema = z.object({
  id: z.string().optional(),
  firstName: z.string().max(120).nullable().optional(),
  lastName: z.string().max(120).nullable().optional(),
  birthDate: z.string().max(20).nullable().optional(),
  nationality: z.string().max(80).nullable().optional(),
  // Entry demographics (B5.4) — the arrival-card portals require them; "" → null.
  gender: z
    .preprocess((v) => (v === "" ? null : v), z.enum(["male", "female", "other"]).nullable())
    .optional(),
  occupation: z.string().max(120).nullable().optional(),
  passportNumber: z.string().max(50).nullable().optional(),
  passportExpiry: z.string().max(20).nullable().optional(),
  dietary: z.string().max(1000).nullable().optional(),
});

// Customer submits the full traveller list from the portal (full sync via syncParticipants).
export const portalParticipantsSubmitSchema = z.object({
  participants: z.array(portalParticipantSchema).max(50),
});

// Public magic-link request: prove order ownership by (orderNumber + email) → emailed link.
// Always answered with success (never leaks whether the pair matched).
export const portalMagicLinkSchema = z.object({
  email: z.string().email().max(200),
  orderNumber: z.string().min(3).max(60),
});

// Admin mints a portal/offer link for an order (returned as a copyable URL).
export const portalLinkRequestSchema = z.object({
  scope: z.enum(["portal", "offer"]).default("portal"),
  expiresInDays: safeIntNullable.pipe(z.number().min(1).max(365).nullable().optional()),
});

export type PortalParticipantInput = z.infer<typeof portalParticipantSchema>;
export type PortalParticipantsSubmitInput = z.infer<typeof portalParticipantsSubmitSchema>;
export type PortalMagicLinkInput = z.infer<typeof portalMagicLinkSchema>;
export type PortalLinkRequestInput = z.infer<typeof portalLinkRequestSchema>;

// ============================================================================
// GUIDE PWA SCHEMAS (Block 2 B2.6 — token-gated, offline-first; all carry an optional
// `clientId` so the PWA's IndexedDB outbox can replay a write idempotently).
// ============================================================================

// Guide ticks a traveller present (or clears it) at the departure.
export const guideCheckinSchema = z.object({
  participantId: z.string().min(1),
  checkedIn: z.boolean(),
});

// Guide logs a field incident (delay/illness/change…) against the departure / one order.
export const guideIncidentSchema = z.object({
  type: z.enum(INCIDENT_TYPES).default("other"),
  severity: z.enum(INCIDENT_SEVERITIES).default("low"),
  note: z.string().min(1).max(4000),
  orderId: nullableId,
  clientId: z.string().max(80).optional(),
});

// Guide reports a cash field expense → booked into the ledger as payments(out, cash).
// `receipt` is an optional base64 data URL of the receipt photo (stored in R2).
export const guideExpenseSchema = z.object({
  amount: safeInt(0).pipe(z.number().min(1)),
  currency: z.string().min(3).max(3).default("CZK"),
  category: z.enum(EXPENSE_CATEGORIES).default("other"),
  note: z.string().max(2000).nullable().optional(),
  supplierName: z.string().max(200).nullable().optional(),
  receipt: z.string().max(8_000_000).nullable().optional(), // base64 data URL, ≤ ~6MB
  clientId: z.string().max(80).optional(),
});

// Guide shares a daily trip photo (Pre-launch Area 5a). `image` is a client-downscaled
// (~1600px) JPEG data URL routed through the media pipeline. photoDate = device-local day.
export const guidePhotoSchema = z.object({
  image: z.string().max(8_000_000), // base64 data URL, ≤ ~6MB
  caption: z.string().max(500).nullable().optional(),
  photoDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  clientId: z.string().max(80).optional(),
});

// Guide broadcasts a message to every traveller on the departure (online only).
export const guideBroadcastSchema = z.object({
  subject: z.string().min(1).max(200),
  body: z.string().min(1).max(8000),
});

// Guide writes/clears the personal message shown to travellers on the portal (Block 4.3 / 4.2
// dotažení). Reveals only once the guide is the confirmed lead. null clears it.
export const guideMessageUpdateSchema = z.object({
  message: z.string().max(2000).nullable(),
  clientId: z.string().max(80).optional(),
});

export type GuideCheckinInput = z.infer<typeof guideCheckinSchema>;
export type GuideIncidentInput = z.infer<typeof guideIncidentSchema>;
export type GuideExpenseInput = z.infer<typeof guideExpenseSchema>;
export type GuidePhotoInput = z.infer<typeof guidePhotoSchema>;
export type GuideBroadcastInput = z.infer<typeof guideBroadcastSchema>;
export type GuideMessageUpdateInput = z.infer<typeof guideMessageUpdateSchema>;

// ============================================================================
// B2.7 — CANCELLATION + COMPLAINTS
// ============================================================================

// Confirm a cancellation. `waiveFeePct` (0–100) lets the garant forgive part/all of the storno
// fee; omitted = charge the policy tier in full.
export const cancellationRequestSchema = z.object({
  reason: z.string().max(2000).nullable().optional(),
  waiveFeePct: safeIntNullable.pipe(z.number().min(0).max(100).nullable().optional()),
});

export const complaintCreateSchema = z.object({
  orderId: nullableId,
  customerId: nullableId,
  type: z.enum(COMPLAINT_TYPES).default("other"),
  status: z.enum(COMPLAINT_STATUSES).default("open"),
  title: z.string().min(1).max(200),
  description: z.string().max(8000).nullable().optional(),
  compensationAmount: safeIntNullable.pipe(z.number().min(0).nullable().optional()),
  currency: z.string().min(3).max(3).default("CZK"),
  dueDate: z.string().max(40).nullable().optional(),
  assignedTo: nullableId,
});

export const complaintUpdateSchema = complaintCreateSchema.partial();

// Resolve a complaint: optionally book a compensation refund (payments out + optional credit note).
export const complaintResolveSchema = z.object({
  status: z.enum(["resolved", "rejected"]).default("resolved"),
  resolutionNote: z.string().max(4000).nullable().optional(),
  compensationAmount: safeIntNullable.pipe(z.number().min(0).nullable().optional()),
  issueCreditNote: z.boolean().default(false),
});

export type CancellationRequestInput = z.infer<typeof cancellationRequestSchema>;
export type ComplaintCreateInput = z.infer<typeof complaintCreateSchema>;
export type ComplaintUpdateInput = z.infer<typeof complaintUpdateSchema>;
export type ComplaintResolveInput = z.infer<typeof complaintResolveSchema>;

// ============================================================================
// NUMBER SEQUENCE SCHEMA (Finance → numbering)
// ============================================================================

export const numberSequenceUpsertSchema = z.object({
  scope: z.string().min(1).max(64),
  prefix: z.string().min(1).max(16),
  // The number that will be assigned to the NEXT document for this scope.
  next: safeInt(1).pipe(z.number().min(1)),
});

export type NumberSequenceUpsertInput = z.infer<typeof numberSequenceUpsertSchema>;

// ============================================================================
// FINANCE CONFIG SCHEMA (Block 2 — currency/rates/VAT/retention)
// ============================================================================

// Partial patch — the finance route merges it over stored config. Bank/IČO/DIČ live in
// `company` settings and deposit %/storno tiers in `booking`; finance does not duplicate.
// Empty/NaN numeric inputs → undefined (omitted from the merge), never null/0.
const optionalFinanceNumber = (max: number) =>
  z.preprocess(
    (v) =>
      v === null || v === undefined || v === "" || (typeof v === "number" && Number.isNaN(v))
        ? undefined
        : Number(v),
    z.number().min(0).max(max).optional(),
  );

export const financeSettingsSchema = z.object({
  defaultCurrency: z.string().length(3).optional(),
  rates: z.record(z.string().length(3), z.number().nonnegative()).optional(),
  vatRate: optionalFinanceNumber(100),
  paymentQr: z.enum(["auto", "off"]).optional(),
  passportRetentionDays: optionalFinanceNumber(3650),
  eventRetentionDays: optionalFinanceNumber(3650),
});

export type FinanceSettingsInput = z.infer<typeof financeSettingsSchema>;

// ============================================================================
// NEWSLETTER SCHEMAS
// ============================================================================

export const newsletterSubscribeSchema = z.object({
  email: z.string().email(),
  preferences: z.array(z.string()).optional(),
  source: z.string().default("footer"),
});

export const newsletterCampaignCreateSchema = z.object({
  subject: z.string().min(1).max(200),
  subjectSk: z.string().max(200).optional(),
  content: z.string().min(1),
  contentSk: z.string().optional(),
  aiGenerated: z.boolean().default(false),
  aiPrompt: z.string().optional(),
  scheduledAt: z.coerce.date().optional(),
});

// ============================================================================
// GIFT VOUCHER SCHEMAS
// ============================================================================

export const giftVoucherCreateSchema = z.object({
  amount: z.number().int().min(1000).max(500000),
  currency: z.string().length(3).default("CZK"),
  buyerName: z.string().min(1).max(200),
  buyerEmail: z.string().email(),
  recipientName: z.string().max(200).optional(),
  recipientEmail: z.string().email().optional(),
  personalMessage: z.string().max(500).optional(),
  designTemplate: z.string().default("default"),
  expiresAt: z.coerce.date(),
});

// ============================================================================
// QUIZ SCHEMA
// ============================================================================

// Quiz keys are enforced at the boundary so MegaMenu / FilterSidebar / QuizFlow
// stay in lockstep with the API. Bucket keys come from the shared `tripBuckets`
// constants and carry literal types, so renaming a bucket key without updating
// callers IS a compile error.
import { DURATION_BUCKET_KEYS, PRICE_BUCKET_KEYS } from "../tripBuckets";

export const quizAnswersSchema = z.object({
  type: z.enum(["adventure", "relax", "culture", "nature"]).optional(),
  budget: z.enum(PRICE_BUCKET_KEYS).optional(),
  duration: z.enum(DURATION_BUCKET_KEYS).optional(),
  companion: z.enum(["solo", "couple", "family", "friends"]).optional(),
  region: z.enum(["northern", "central", "southern", "combination"]).optional(),
});

export const quizSubmitSchema = z.object({
  answers: quizAnswersSchema,
  email: z.string().email().optional(),
  subscribeNewsletter: z.boolean().default(false),
});

// ============================================================================
// DESTINATION TEAM SCHEMAS
// ============================================================================

// Blank/null-tolerant — sections load from DB with NULL email/bio/photo and the
// admin form holds "" for untouched inputs; both must validate as "not set".
export const destinationTeamMemberSchema = z.object({
  name: z.string().min(1, "Member name is required"),
  role: z.preprocess(blankToUndefined, z.string().optional()),
  bio: z.preprocess(blankToUndefined, z.string().optional()),
  photo: z.preprocess(blankToUndefined, z.string().optional()),
  photoId: z.string().nullable().optional(),
});

export const destinationTeamCreateSchema = z.object({
  destinationId: z.string().min(1),
  sectionType: z.enum(TEAM_SECTION_TYPES),
  name: z.string().min(1).max(200),
  subtitle: z.preprocess(blankToUndefined, z.string().max(300).optional()),
  bio: z.preprocess(blankToUndefined, z.string().optional()),
  email: z.preprocess(blankToUndefined, z.string().email().optional()),
  phone: z.preprocess(blankToUndefined, z.string().max(30).optional()),
  photo: z.preprocess(blankToUndefined, z.string().optional()),
  photoMediaId: z.string().nullable().optional(),
  photos: z.preprocess((v) => v ?? undefined, z.array(z.string()).optional()),
  photoIds: z.preprocess((v) => v ?? undefined, z.array(z.string()).optional()),
  members: z.preprocess((v) => v ?? undefined, z.array(destinationTeamMemberSchema).optional()),
  sortOrder: safeInt(0),
  isActive: z.boolean().default(true),
});

export const destinationTeamUpdateSchema = destinationTeamCreateSchema.partial();

// ============================================================================
// AUTH SCHEMAS
// ============================================================================

export const loginSchema = z.object({
  email: z.string().email(),
  password: z.string().min(8),
});

export const userCreateSchema = z.object({
  email: z.string().email(),
  password: z.string().min(8),
  name: z.string().min(1).max(200),
  role: z.enum(USER_ROLES).default("editor"),
  phone: z.string().max(50).optional(),
  avatar: z.string().url().optional(),
  isActive: z.boolean().default(true),
  // Specialist queue profile (E2E-018) — service-booking kinds this user works (e.g. ["flight"]).
  queueKinds: z.array(z.enum(SERVICE_KINDS)).optional(),
});

export const userUpdateSchema = z.object({
  email: z.string().email().optional(),
  password: z.string().min(8).optional(),
  name: z.string().min(1).max(200).optional(),
  role: z.enum(USER_ROLES).optional(),
  phone: z.string().max(50).nullable().optional(),
  avatar: z.string().url().nullable().optional(),
  isActive: z.boolean().optional(),
  queueKinds: z.array(z.enum(SERVICE_KINDS)).optional(),
});

export const passwordResetSchema = z.object({
  password: z.string().min(8),
});

// ============================================================================
// REVIEW SCHEMAS
// ============================================================================

export const reviewCreateSchema = z.object({
  tripId: z.string().optional(),
  destinationId: z.string().optional(),
  guideId: z.string().optional(),
  customerName: z.string().min(1).max(200),
  customerLocation: z.string().max(100).optional(),
  rating: z.number().int().min(1).max(5),
  title: z.string().max(200).optional(),
  content: z.string().min(10).max(2000),
  tripDate: z.coerce.date().optional(),
  isVideo: z.boolean().default(false),
  videoUrl: z.string().url().optional(),
  images: z.array(z.string()).optional(),
  imageIds: z.array(z.string()).optional(),
  isFeatured: z.boolean().default(false),
  isApproved: z.boolean().default(false),
});

export const reviewUpdateSchema = reviewCreateSchema.partial();

// ============================================================================
// COMMUNICATION SCHEMAS
// ============================================================================

export const communicationCreateSchema = z.object({
  orderId: z.string().optional(),
  customerId: z.string().min(1),
  type: z.enum(COMMUNICATION_TYPES),
  direction: z.enum(COMMUNICATION_DIRECTIONS),
  subject: z.string().max(500).optional(),
  content: z.string().optional(),
  attachments: z.array(z.string().url()).optional(),
});

// ============================================================================
// PREMIUM UPGRADE SCHEMAS
// ============================================================================

export const premiumUpgradeCreateSchema = z.object({
  title: z.string().min(1).max(200),
  description: z.string().optional(),
  image: z
    .string()
    .optional()
    .transform((v) => (v === "" ? undefined : v)),
  imageId: z.string().nullable().optional(),
  badge: z.string().max(50).optional(),
  sortOrder: safeInt(0),
  isActive: z.boolean().default(true),
});

export const premiumUpgradeUpdateSchema = premiumUpgradeCreateSchema.partial();

// ============================================================================
// FAQ SCHEMAS (central FAQ table)
// ============================================================================

export const FAQ_STATUSES = ["draft", "published"] as const;

export const faqCreateSchema = z.object({
  question: z.string().min(1).max(500),
  answer: z.string().min(1),
  topic: z.string().min(1).max(200),
  topicSortOrder: safeInt(0),
  sortOrder: safeInt(0),
  status: z.enum(FAQ_STATUSES).default("published"),
});

export const faqUpdateSchema = faqCreateSchema.partial();

// ============================================================================
// TYPE EXPORTS
// ============================================================================

export type PaginationInput = z.infer<typeof paginationSchema>;
export type InquiryInput = z.infer<typeof inquirySchema>;
export type TripFilterInput = z.infer<typeof tripFilterSchema>;
export type NewsletterSubscribeInput = z.infer<typeof newsletterSubscribeSchema>;
export type QuizSubmitInput = z.infer<typeof quizSubmitSchema>;
export type LoginInput = z.infer<typeof loginSchema>;
export type DestinationCreateInput = z.infer<typeof destinationCreateSchema>;
export type GuideCreateInput = z.infer<typeof guideCreateSchema>;
export type TripCreateInput = z.infer<typeof tripCreateSchema>;
export type TripDateCreateInput = z.infer<typeof tripDateCreateSchema>;
export type CustomerCreateInput = z.infer<typeof customerCreateSchema>;
export type OrderCreateInput = z.infer<typeof orderCreateSchema>;
export type ReviewCreateInput = z.infer<typeof reviewCreateSchema>;
export type GiftVoucherCreateInput = z.infer<typeof giftVoucherCreateSchema>;
export type ActivityCreateInput = z.infer<typeof activityCreateSchema>;
export type ActivityFilterInput = z.infer<typeof activityFilterSchema>;
export type BlogPostCreateInput = z.infer<typeof blogPostCreateSchema>;
export type BlogPostFilterInput = z.infer<typeof blogPostFilterSchema>;
export type DestinationTeamCreateInput = z.infer<typeof destinationTeamCreateSchema>;
export type PremiumUpgradeCreateInput = z.infer<typeof premiumUpgradeCreateSchema>;
export type FaqCreateInput = z.infer<typeof faqCreateSchema>;

// Aliases for backward compatibility
export const destinationSchema = destinationCreateSchema;
export const guideSchema = guideCreateSchema;
export const tripSchema = tripCreateSchema;
export const tripDateSchema = tripDateCreateSchema;
export const customerSchema = customerCreateSchema;
export const orderSchema = orderCreateSchema;
export const reviewSchema = reviewCreateSchema;

// Wiki / internal knowledge base (Block 3 B3.7)
export const wikiPageCreateSchema = z.object({
  title: z.string().min(1).max(200),
  slug: z.string().max(200).optional(),
  destinationId: z
    .string()
    .nullable()
    .optional()
    .or(z.literal("").transform(() => null)),
  category: z.enum(["playbook", "contacts", "sop", "general"]).default("general"),
  body: z.string().default(""),
  status: z.enum(["draft", "published"]).default("draft"),
});
export const wikiPageUpdateSchema = wikiPageCreateSchema.partial();
export type WikiPageInput = z.infer<typeof wikiPageCreateSchema>;

// Country knowledge base (Block 5.6 Řez 1) — structured per-country facts feeding the
// offer PDF, pre-departure pack, portal checklist, arrival cards and the AI sales layer.
const seasonalityMonthSchema = z.object({
  score: z.enum(["best", "good", "poor"]),
  note: z.string().max(300).optional(),
});
export const countryRequirementsCreateSchema = z.object({
  countryCode: z
    .string()
    .length(2)
    .transform((s) => s.toUpperCase()),
  countryName: z.string().min(1).max(100),
  visaPolicy: z.enum(["visa_free", "visa_on_arrival", "e_visa", "embassy"]).nullable().optional(),
  visaFreeDays: safeIntNullable.pipe(z.number().min(0).nullable().optional()),
  visaFee: z.string().max(300).nullable().optional(),
  visaUrl: z.string().max(500).nullable().optional(),
  visaNotes: z.string().nullable().optional(),
  passportMinValidityMonths: safeIntNullable.pipe(z.number().min(0).max(24).nullable().optional()),
  vaccinationRequired: z.string().max(500).nullable().optional(),
  healthNotes: z.string().nullable().optional(),
  arrivalCard: z.enum(["none", "on_arrival", "online"]).nullable().optional(),
  arrivalCardUrl: z.string().max(500).nullable().optional(),
  arrivalCardWindowDays: safeIntNullable.pipe(z.number().min(0).max(365).nullable().optional()),
  arrivalCardNotes: z.string().nullable().optional(),
  // Entry engine config (Block 5.4)
  arrivalCardGroupMax: safeIntNullable.pipe(z.number().min(1).max(100).nullable().optional()),
  arrivalCardArtifacts: z.array(z.string().max(50)).nullable().optional(),
  arrivalCardChannel: z.enum(["manual", "browser"]).optional(),
  arrivalCardAutoEnabled: z.boolean().optional(),
  visaFilingLeadDays: safeIntNullable.pipe(z.number().min(1).max(365).nullable().optional()),
  seasonality: z.record(z.string(), seasonalityMonthSchema).nullable().optional(),
  currencyInfo: z.string().max(500).nullable().optional(),
  plugs: z.string().max(300).nullable().optional(),
  tipping: z.string().max(500).nullable().optional(),
  safetyNotes: z.string().nullable().optional(),
  practicalNotes: z.string().max(5000).nullable().optional(),
  isActive: z.boolean().default(true),
});
export const countryRequirementsUpdateSchema = countryRequirementsCreateSchema.partial();
export type CountryRequirementsInput = z.infer<typeof countryRequirementsCreateSchema>;

// Type aliases for backward compatibility
export type DestinationInput = DestinationCreateInput;
export type GuideInput = GuideCreateInput;
export type TripInput = TripCreateInput;
export type TripDateInput = TripDateCreateInput;
export type CustomerInput = CustomerCreateInput;
export type OrderInput = OrderCreateInput;
export type ReviewInput = ReviewCreateInput;

// Journeys — universal automation engine (B3 řez 2)
export * from "./journeys";

// Newsletter edition sections + zine themes (newsletter system, K2)
export * from "./newsletterSections";
